import { useEffect } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import { DEFAULT_FORMAT_SETTINGS } from '@nesso/session-contracts/config';
import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';

import { sessionByIdOptions, useResolveActionOptions } from '@/entities/session';
import { useIsGenerating, useIsGenerationAvailable } from '@/entities/session-note';
import { EditSessionNoteSettings } from '@/features/session-note/edit-session-note-settings';
import { isNotNullish } from '@/shared/lib/object-utils';

import { useSessionId } from '../../model/use-session-id';
import { useSessionNoteAutosave } from '../../model/use-session-note-autosave';
import { GenerateSessionNote } from './session-note/generate-session-note';
import { NessoSessionNote } from './session-note/nesso-session-note';
import { SessionNoteAiDisclaimer } from './session-note/session-note-ai-disclaimer';
import { SessionNoteDeleted } from './session-note/session-note-deleted';
import { SessionNoteError } from './session-note/session-note-error';
import { SessionNoteFooter } from './session-note/session-note-footer';
import { SessionNoteHeader } from './session-note/session-note-header';
import { SessionNoteLoader } from './session-note/session-note-loader';
import { SoapSessionNote } from './session-note/soap-session-note';
import { SoapSessionNoteTreatmentApproaches } from './session-note/soap-session-note-treatment-approaches';

export function SessionNote() {
    const sessionId = useSessionId();
    const isSm = useBreakpointDown('sm');

    const { data: session } = useQuery(sessionByIdOptions(sessionId));

    const { saveContent, isContentSaving } = useSessionNoteAutosave(sessionId);

    const note = session?.currentNote;
    const resolveSessionActionMutation = useMutation(
        useResolveActionOptions(sessionId, 'REVIEW_NOTE'),
    );

    useEffect(() => {
        if (session?.actions.includes('REVIEW_NOTE')) {
            resolveSessionActionMutation.mutate();
        }
    }, [session?.id, session?.actions]);

    const isGenerating = useIsGenerating(sessionId);
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);

    if (!session) return null;

    const generationErrorType = note?.generator?.errorType ?? null;
    const hasGenerationError = generationErrorType !== null;
    const hasContent = isNotNullish(note?.content);
    const isSessionNoteDeleted =
        session.generatedNoteDeletedAt !== null && session.transcriptDeletedAt !== null;
    const settings = note?.generator?.settings ?? DEFAULT_FORMAT_SETTINGS;
    const headerAlert = match({ hasGenerationError, isSessionNoteDeleted })
        .with({ hasGenerationError: true }, () => (
            <SessionNoteError errorType={generationErrorType} />
        ))
        .with({ isSessionNoteDeleted: true }, () => (
            <SessionNoteDeleted deletedAt={session.generatedNoteDeletedAt} />
        ))
        .otherwise(() => undefined);

    const isGenerated =
        !note?.generator?.errorType && !!note?.generator?.generatedAt && !!note?.content;
    const disclaimer = isGenerated ? (
        <SessionNoteAiDisclaimer generatedAt={note?.generator?.generatedAt} />
    ) : null;
    const headerActions = isSm ? <EditSessionNoteSettings session={session} /> : undefined;

    return (
        <>
            <div className="pt-6 pb-24 px-4 sm:pt-14 sm:pb-28 sm:px-16 h-full overflow-y-auto space-y-10 sm:space-y-6">
                {match({
                    isGenerating,
                    hasContent,
                    isGenerationAvailable,
                    hasGenerationError,
                    type: note?.type,
                })
                    .with({ isGenerating: true }, () => (
                        <SessionNoteLoader
                            title="Generating note..."
                            description="You can close this session while we work"
                        />
                    ))
                    .with(
                        {
                            isGenerationAvailable: true,
                            hasContent: false,
                            hasGenerationError: false,
                        },
                        () => <GenerateSessionNote session={session} />,
                    )
                    .with({ type: 'SOAP' }, () => (
                        <SoapSessionNote
                            session={session}
                            updateContent={saveContent}
                            disclaimer={disclaimer}
                            header={
                                <SessionNoteHeader
                                    title="SOAP"
                                    actions={headerActions}
                                    alert={headerAlert}
                                >
                                    <SoapSessionNoteTreatmentApproaches
                                        sessionId={session.id}
                                        selectedApproaches={session.treatmentApproaches ?? []}
                                        settings={settings}
                                    />
                                </SessionNoteHeader>
                            }
                        />
                    ))
                    .with({ type: 'NESSO' }, () => (
                        <NessoSessionNote
                            session={session}
                            updateContent={saveContent}
                            disclaimer={disclaimer}
                            header={
                                <SessionNoteHeader
                                    title="Progress Note"
                                    actions={headerActions}
                                    alert={headerAlert}
                                />
                            }
                        />
                    ))
                    .otherwise(() => null)}
            </div>
            <SessionNoteFooter isNoteSaving={isContentSaving} />
        </>
    );
}
